// Pages/NotificationsPage.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, ArrowLeft } from 'lucide-react';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  fetchUnreadCount,
  fetchNotificationCounts,
  clearNotifications,
} from '../features/notifications/notificationSlice';
import useNotifications from '../hooks/useNotifications';
import Sidebar from '../Component/Sidebar';

const NotificationsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  
  const { notifications, unreadCount, loading, hasMore, counts } = useSelector(
    (state) => state.notifications
  );
  
  const { markAsRead, markAllAsRead } = useNotifications();

  // Fetch notifications when filter changes
  useEffect(() => {
    // Map filter -> is_read param for API
    let is_read = null;
    if (filter === 'unread') is_read = false;
    if (filter === 'read') is_read = true;

    // Reset list & pagination whenever filter changes
    dispatch(clearNotifications());

    // Fetch first page for this filter
    dispatch(fetchNotifications({ limit: 50, offset: 0, is_read }));

    // Keep header unread count and global counts in sync
    dispatch(fetchUnreadCount());
    dispatch(fetchNotificationCounts());
  }, [dispatch, filter]);

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      // Mark as read via WebSocket
      markAsRead(notification.id);
      
      // Also mark via REST API as backup
      dispatch(markNotificationRead(notification.id));
    }
    
    // Navigate based on notification type
    handleNotificationNavigation(notification);
  };

  const handleMarkAllRead = () => {
    markAllAsRead();
    dispatch(markAllNotificationsRead());
  };

  const handleNotificationNavigation = (notification) => {
    // Navigate to relevant page based on notification type
    switch (notification.notification_type) {
      case 'REPORT_UPDATE':
      case 'REPORT_APPROVED':
      case 'COMMENT_MENTION':
        if (notification.report) {
          navigate(`/reports/${notification.report}/edit`);
        }
        else if (notification.extra_data?.transaction_id) {
          navigate(`/transaction/${notification.extra_data.transaction_id}`);
        }
        break;
      
      case 'CLIENT_QUERY_CREATED':
      case 'CLIENT_QUERY_UPDATE':
        if (notification.query) {
          navigate(`/client-queries/${notification.query}/edit`);
        } else {
          navigate('/clientqueriestable');
        }
        break;
      
      case 'STORE_UPDATE':
        if (notification.extra_data?.store_id) {
          navigate(`/stores/${notification.extra_data.store_id}`);
        } else {
          navigate('/stores');
        }
        break;
      
      case 'EMPLOYEE_UPDATE':
        if (notification.extra_data?.employee_id) {
          navigate(`/employees/${notification.extra_data.employee_id}`);
        } else {
          navigate('/employeecards');
        }
        break;
      
      case 'CASHIER_ASSISTANCE':
        navigate('/cashier-assistance');
        break;

      case 'SUSPICIOUS_TRANSACTION':
        navigate('/suspicioustransactionalerts');
        break;
      
      case 'TRIGGER_ALERT':
        navigate('/triggeralerts');
        break;
      
      default:
        navigate('/dashboard');
        break;
    }
  };

  const getNotificationIcon = (type) => {
    const colors = {
      REPORT_UPDATE: 'bg-blue-500',
      COMMENT_MENTION: 'bg-purple-500',
      REPORT_APPROVED: 'bg-green-500',
      CLIENT_QUERY_CREATED: 'bg-orange-500',
      CLIENT_QUERY_UPDATE: 'bg-orange-400',
      STORE_UPDATE: 'bg-indigo-500',
      EMPLOYEE_UPDATE: 'bg-teal-500',
      CASHIER_ASSISTANCE: 'bg-amber-500',
      SUSPICIOUS_TRANSACTION: 'bg-red-500',
      TRIGGER_ALERT: 'bg-yellow-500',
    };
    return colors[type] || 'bg-gray-500';
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      REPORT_UPDATE: 'Report Update',
      COMMENT_MENTION: 'Mention',
      REPORT_APPROVED: 'Report Approved',
      CLIENT_QUERY_CREATED: 'New Query',
      CLIENT_QUERY_UPDATE: 'Query Update',
      STORE_UPDATE: 'Store Update',
      EMPLOYEE_UPDATE: 'Employee Update',
      CASHIER_ASSISTANCE: 'Cashier Assistance',
      SUSPICIOUS_TRANSACTION: 'Suspicious Activity',
      TRIGGER_ALERT: 'Alert',
    };
    return labels[type] || 'Notification';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatFullTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Global counts from backend (not limited by pagination)
  const allCount = counts.total;
  const unreadFilterCount = counts.unread;
  const readCount = counts.read;

  const loadMore = () => {
    if (!loading && hasMore) {
      let is_read = null;
      if (filter === 'unread') is_read = false;
      if (filter === 'read') is_read = true;

      dispatch(fetchNotifications({
        limit: 50,
        offset: notifications.length,
        is_read,
      }));
    }
  };

  return (
    <div className='index-box'>
      <Sidebar />
      <div className="notifications-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-left">
            <button 
              className="back-button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="header-title-section">
              <h1 className="page-title">Notifications</h1>
              <span className="notification-count">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </span>
            </div>
          </div>

          <div className="header-actions">
            {unreadCount > 0 && (
              <button
                className="mark-all-read-button"
                onClick={handleMarkAllRead}
              >
                <CheckCheck size={18} />
                Mark all as read
              </button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
            <span className="tab-count">{allCount}</span>
          </button>
          <button
            className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Unread
            {unreadFilterCount > 0 && (
              <span className="tab-count unread-badge">{unreadFilterCount}</span>
            )}
          </button>
          <button
            className={`filter-tab ${filter === 'read' ? 'active' : ''}`}
            onClick={() => setFilter('read')}
          >
            Read
            <span className="tab-count">{readCount}</span>
          </button>
        </div>

        {/* Notifications List */}
        <div className="notifications-container">
          {loading && notifications.length === 0 ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="empty-state">
              <Bell size={64} className="empty-icon" />
              <h3>No {filter !== 'all' ? filter : ''} notifications</h3>
              <p>
                {filter === 'unread' 
                  ? "You're all caught up! No unread notifications." 
                  : filter === 'read'
                  ? "No read notifications yet."
                  : "You don't have any notifications yet."}
              </p>
            </div>
          ) : (
            <>
              <div className="notifications-list">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-card ${
                      notification.is_read ? 'read' : 'unread'
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {/* Icon */}
                    <div className={`notification-icon-wrapper ${getNotificationIcon(notification.notification_type)}`}>
                      <Bell size={24} className="notification-bell-icon" />
                      {!notification.is_read && <div className="unread-indicator" />}
                    </div>

                    {/* Content */}
                    <div className="notification-content">
                      <div className="notification-header">
                        <span className="notification-type-badge">
                          {getNotificationTypeLabel(notification.notification_type)}
                        </span>
                        <span className="notification-time" title={formatFullTime(notification.created_at)}>
                          {formatTime(notification.created_at)}
                        </span>
                      </div>
                      <h3 className="notification-title">{notification.title}</h3>
                      <p className="notification-message">{notification.message}</p>
                    </div>

                    {/* Actions */}
                    <div className="notification-actions">
                      {!notification.is_read && (
                        <button
                          className="mark-read-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                            dispatch(markNotificationRead(notification.id));
                          }}
                          title="Mark as read"
                        >
                          <Check size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="load-more-section">
                  <button
                    className="load-more-button"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load more notifications'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <style jsx>{`
        

          .notifications-page {
            flex: 1;
            background: #f9fafb;
            min-height: 100vh;
            border-radius:20px;
          }

          .page-header {
                      border-radius:20px 20px 0 0;

            background: white;
            padding: 20px 32px;
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            align-items: center;
            justify-content: space-between;
            position: sticky;
            top: 0;
            z-index: 10;
          }

          .header-left {
            display: flex;
            align-items: center;
            gap: 16px;
          }

          .back-button {
            padding: 8px;
            border: none;
            background: transparent;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6b7280;
            transition: all 0.2s;
          }

          .back-button:hover {
            background: #f3f4f6;
            color: #111827;
          }

          .header-title-section {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .page-title {
            margin: 0;
            font-size: 24px;
            font-weight: 700;
            color: #111827;
          }

          .notification-count {
            font-size: 14px;
            color: #6b7280;
            font-weight: 500;
          }

          .header-actions {
            display: flex;
            gap: 12px;
          }

          .mark-all-read-button {
            padding: 10px 16px;
            border: 1px solid #e5e7eb;
            background: white;
            color: #374151;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s;
          }

          .mark-all-read-button:hover {
            background: #f9fafb;
            border-color: #f43f5e;
            color: #f43f5e;
          }

          .filter-tabs {
            background: white;
            padding: 0 32px;
            display: flex;
            gap: 32px;
            border-bottom: 1px solid #e5e7eb;
            position: sticky;
            top: 81px;
            z-index: 9;
          }

          .filter-tab {
            padding: 16px 0;
            border: none;
            background: transparent;
            color: #6b7280;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            position: relative;
            transition: color 0.2s;
          }

          .filter-tab:hover {
            color: #111827;
          }

          .filter-tab.active {
            color: #f43f5e;
          }

          .filter-tab.active::after {
            content: '';
            position: absolute;
            bottom: -1px;
            left: 0;
            right: 0;
            height: 2px;
            background: #f43f5e;
          }

          .tab-count {
            padding: 2px 8px;
            background: #f3f4f6;
            color: #6b7280;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
            min-width: 20px;
            text-align: center;
          }

          .tab-count.unread-badge {
            background: #fee2e2;
            color: #ef4444;
          }

          .filter-tab.active .tab-count {
            background: #fee2e2;
            color: #f43f5e;
          }

          .notifications-container {
            max-width: 900px;
            margin: 0 auto;
            padding: 24px 32px;
          }

      .notifications-container .notifications-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
            max-height: 80%;
          }

          .notification-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 20px;
            display: flex;
            gap: 16px;
            cursor: pointer;
            transition: all 0.2s;
            position: relative;
          }

          .notification-card:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            transform: translateY(-2px);
          }

          .notification-card.unread {
            background: #eff6ff;
            border-color: #bfdbfe;
            border-width: 2px;
          }

          .notification-card.read {
            background: white;
            opacity: 0.85;
          }

          .notification-icon-wrapper {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            position: relative;
          }

          .notification-bell-icon {
            color: white;
          }

          .unread-indicator {
            position: absolute;
            top: -2px;
            right: -2px;
            width: 14px;
            height: 14px;
            background: #ef4444;
            border: 3px solid white;
            border-radius: 50%;
            animation: pulse 2s infinite;
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }

          .notification-content {
            flex: 1;
            min-width: 0;
          }

          .notification-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
            gap: 12px;
          }

          .notification-type-badge {
            padding: 4px 10px;
            background: #f3f4f6;
            color: #6b7280;
            border-radius: 6px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .notification-time {
            font-size: 13px;
            color: #9ca3af;
            white-space: nowrap;
          }

          .notification-title {
            margin: 0 0 8px 0;
            font-size: 16px;
            font-weight: 600;
            color: #111827;
            line-height: 1.4;
          }

          .notification-message {
            margin: 0;
            font-size: 14px;
            color: #6b7280;
            line-height: 1.5;
          }

          .notification-actions {
            display: flex;
            align-items: flex-start;
            gap: 8px;
          }

          .mark-read-icon-btn {
            padding: 8px;
            border: none;
            background: transparent;
            color: #9ca3af;
            cursor: pointer;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
          }

          .mark-read-icon-btn:hover {
            background: #f3f4f6;
            color: #f43f5e;
          }

          .loading-state,
          .empty-state {
            padding: 80px 20px;
            text-align: center;
          }

          .spinner {
            width: 40px;
            height: 40px;
            margin: 0 auto 20px;
            border: 3px solid #e5e7eb;
            border-top-color: #f43f5e;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .loading-state p {
            color: #6b7280;
            font-size: 14px;
          }

          .empty-icon {
            margin: 0 auto 20px;
            color: #d1d5db;
          }

          .empty-state h3 {
            margin: 0 0 8px 0;
            font-size: 20px;
            font-weight: 600;
            color: #111827;
          }

          .empty-state p {
            margin: 0;
            font-size: 14px;
            color: #6b7280;
          }

          .load-more-section {
            margin-top: 24px;
            text-align: center;
          }

          .load-more-button {
            padding: 12px 24px;
            border: 1px solid #e5e7eb;
            background: white;
            color: #374151;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .load-more-button:hover:not(:disabled) {
            background: #f9fafb;
            border-color: #f43f5e;
            color: #f43f5e;
          }

          .load-more-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          /* Responsive Design */
          @media (max-width: 768px) {
            .page-header {
              padding: 16px 20px;
            }

            .page-title {
              font-size: 20px;
            }

            .header-actions {
              display: none;
            }

            .filter-tabs {
              padding: 0 20px;
              gap: 24px;
            }

            .notifications-container {
              padding: 16px 20px;
            }

            .notification-card {
              padding: 16px;
              gap: 12px;
            }

            .notification-icon-wrapper {
              width: 40px;
              height: 40px;
            }

            .notification-title {
              font-size: 15px;
            }

            .notification-message {
              font-size: 13px;
            }
          }

          /* Color variants for notification types */
          .bg-blue-500 { background: #f43f5e; }
          .bg-purple-500 { background: #a855f7; }
          .bg-green-500 { background: #22c55e; }
          .bg-orange-500 { background: #f97316; }
          .bg-orange-400 { background: #fb923c; }
          .bg-indigo-500 { background: #6366f1; }
          .bg-teal-500 { background: #14b8a6; }
          .bg-red-500 { background: #ef4444; }
          .bg-yellow-500 { background: #eab308; }
          .bg-gray-500 { background: #6b7280; }
        `}</style>
      </div>
    </div>
  );
};

export default NotificationsPage;